'use client';
/**
 * components/UrgenceSheet.tsx — OGRYN
 *
 * Bottom sheet "Intervention urgente" pour les clients.
 * - Description texte + dictée vocale (Web Speech API, fr-FR)
 * - Type de prestataire recherché
 * - Photos / vidéos (≤ 5 Mo par fichier)
 *
 * ⚠️ SA SOUMISSION NE MÈNE NULLE PART (constaté le 01/09).
 * Elle émet `arthi:open`, un évènement que plus personne n'écoute : le widget
 * de chat de RapidPro qui l'attendait a été supprimé, et il appelait de toute
 * façon `/api/arthi`, route qui n'a jamais existé sur OGRYN. L'écran affiche
 * pourtant « Demande transmise ».
 *
 * Sans conséquence aujourd'hui : ce composant n'est monté que derrière
 * `SHOW_LEGACY_FABS` (MobileShell), qui est à `false`. AVANT DE LE RALLUMER,
 * brancher la soumission sur Otto (`OttoChat`, `/api/otto`) — sinon l'usager
 * croit avoir envoyé une demande urgente qui n'est partie nulle part.
 *
 * ⚠️ Aucun import Leaflet.
 */

import { useState, useRef, useCallback, useEffect } from 'react';

interface Props {
  onClose: () => void;
}

// ── Types d'prestataires ───────────────────────────────────────────────────────────

const PRESTATAIRE_TYPES = [
  'Électricien', 'Plombier', 'Serrurier', 'Chauffagiste', 'Maçon',
  'Menuisier', 'Vitrier', 'Couvreur', 'Peintre', 'Chauffeur',
  'Autre / Je ne sais pas',
];

// ── SVG icons ──────────────────────────────────────────────────────────────────

function IconMic({ active }: { active: boolean }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
      stroke={active ? '#f87171' : 'currentColor'} strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
      <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
      <line x1="12" y1="19" x2="12" y2="23"/>
      <line x1="8" y1="23" x2="16" y2="23"/>
    </svg>
  );
}

function IconPhoto() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2"/>
      <circle cx="8.5" cy="8.5" r="1.5"/>
      <polyline points="21 15 16 10 5 21"/>
    </svg>
  );
}

function IconClose() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  );
}

function IconSend() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
    </svg>
  );
}

// ── Web Speech API ─────────────────────────────────────────────────────────────

// Web Speech API — types minimaux (non standard dans TypeScript strict)
interface ISpeechRecognitionEvent extends Event {
  resultIndex: number;
  results: { [i: number]: { [j: number]: { transcript: string }; isFinal: boolean; length: number }; length: number };
}
interface ISpeechRecognition extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onstart:  ((this: ISpeechRecognition, ev: Event) => void) | null;
  onend:    ((this: ISpeechRecognition, ev: Event) => void) | null;
  onerror:  ((this: ISpeechRecognition, ev: Event) => void) | null;
  onresult: ((this: ISpeechRecognition, ev: ISpeechRecognitionEvent) => void) | null;
  start(): void;
  stop():  void;
}
declare global {
  interface Window {
    SpeechRecognition:       new () => ISpeechRecognition;
    webkitSpeechRecognition: new () => ISpeechRecognition;
  }
}

// ── Component ──────────────────────────────────────────────────────────────────

export default function UrgenceSheet({ onClose }: Props) {
  const [description,  setDescription]  = useState('');
  const [prestataireType,  setPrestataireType]  = useState('');
  const [listening,    setListening]    = useState(false);
  const [interimText,  setInterimText]  = useState('');
  const [files,        setFiles]        = useState<File[]>([]);
  const [fileError,    setFileError]    = useState('');
  const [success,      setSuccess]      = useState(false);

  const recognitionRef = useRef<ISpeechRecognition | null>(null);
  const fileInputRef   = useRef<HTMLInputElement>(null);

  // Nettoie la reconnaissance au démontage
  useEffect(() => {
    return () => { recognitionRef.current?.stop(); };
  }, []);

  // ── Dictée vocale (Web Speech API) ────────────────────────────────────────

  const toggleDictation = useCallback(() => {
    const SpeechRec = (window.SpeechRecognition ?? window.webkitSpeechRecognition);
    if (!SpeechRec) {
      alert('La reconnaissance vocale n\'est pas disponible sur ce navigateur.');
      return;
    }

    if (listening) {
      recognitionRef.current?.stop();
      return;
    }

    const recognition = new SpeechRec();
    recognition.lang = 'fr-FR';
    recognition.continuous = false;
    recognition.interimResults = true;
    recognitionRef.current = recognition;

    recognition.onstart  = () => setListening(true);
    recognition.onend    = () => { setListening(false); setInterimText(''); };
    recognition.onerror  = () => { setListening(false); setInterimText(''); };

    recognition.onresult = (event: ISpeechRecognitionEvent) => {
      let interim = '';
      let final   = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const t = event.results[i][0].transcript;
        if (event.results[i].isFinal) final += t;
        else interim += t;
      }
      if (final) setDescription(prev => (prev ? prev + ' ' + final : final).trim());
      setInterimText(interim);
    };

    recognition.start();
  }, [listening]);

  // ── Fichiers joints ────────────────────────────────────────────────────────

  const handleFiles = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFileError('');
    const selected = Array.from(e.target.files ?? []);
    const MAX_MB = 5;
    const tooLarge = selected.filter(f => f.size > MAX_MB * 1024 * 1024);
    if (tooLarge.length > 0) {
      setFileError(`${tooLarge.map(f => f.name).join(', ')} dépasse ${MAX_MB} Mo — ignoré(s)`);
    }
    const valid = selected.filter(f => f.size <= MAX_MB * 1024 * 1024);
    setFiles(prev => [...prev, ...valid].slice(0, 5)); // max 5 fichiers
    e.target.value = '';
  }, []);

  const removeFile = useCallback((i: number) => {
    setFiles(prev => prev.filter((_, idx) => idx !== i));
  }, []);

  // ── Soumission ─────────────────────────────────────────────────────────────

  const submit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (!description.trim()) return;

    // Évènement historique, sans écouteur — cf. l'avertissement en tête.
    const msg = [
      `[URGENCE] ${prestataireType ? prestataireType + ' — ' : ''}${description.trim()}`,
      files.length > 0 ? `(${files.length} photo(s) jointe(s))` : '',
    ].filter(Boolean).join(' ');

    window.dispatchEvent(new CustomEvent('arthi:open', { detail: { message: msg } }));
    setSuccess(true);
  }, [description, prestataireType, files]);

  const inputStyle: React.CSSProperties = {
    width: '100%', background: 'rgba(255,255,255,.06)',
    border: '0.5px solid rgba(255,255,255,.12)', borderRadius: 10,
    padding: '10px 12px', color: 'rgba(255,255,255,.88)',
    fontSize: 16, outline: 'none', boxSizing: 'border-box',
  };

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 900 }}
      />

      {/* Sheet */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        background: 'rgba(10,6,3,.98)',
        borderRadius: '18px 18px 0 0',
        border: '0.5px solid rgba(5,150,105,.2)',
        zIndex: 901,
        padding: '10px 0 40px',
        maxHeight: '90vh',
        overflowY: 'auto',
        scrollbarWidth: 'none',
      }}>
        {/* Handle */}
        <div style={{ width: 32, height: 3, background: 'rgba(255,255,255,.18)', borderRadius: 2, margin: '0 auto 12px' }} />

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '0 16px 14px', borderBottom: '0.5px solid rgba(255,255,255,.07)' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 900, color: '#fff' }}>Intervention urgente</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,.4)', marginTop: 2 }}>
              Décrivez votre besoin — un prestataire sera contacté sous peu
            </div>
          </div>
          <button type="button" onClick={onClose}
            style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(255,255,255,.06)', border: 'none', color: 'rgba(255,255,255,.5)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <IconClose />
          </button>
        </div>

        {success ? (
          <div style={{ padding: '40px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: 36, marginBottom: 16 }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto' }}>
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#fff', marginBottom: 8 }}>Demande transmise</div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,.45)', lineHeight: 1.5, marginBottom: 24 }}>
              Notre agent IA analyse votre demande et contacte les prestataires disponibles près de chez vous.
            </div>
            <button type="button" onClick={onClose}
              style={{ background: '#2E7D52', border: 'none', borderRadius: 12, padding: '12px 32px', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
              Fermer
            </button>
          </div>
        ) : (
          <form onSubmit={submit} style={{ padding: '16px 16px 8px' }}>

            {/* Description + micro */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <label style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,.38)', textTransform: 'uppercase', letterSpacing: '.07em' }}>
                  Décrivez votre problème *
                </label>
                <button
                  type="button"
                  onClick={toggleDictation}
                  title={listening ? 'Arrêter la dictée' : 'Dicter'}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    height: 30, padding: '0 10px', borderRadius: 8,
                    background: listening ? 'rgba(220,38,38,.2)' : 'rgba(255,255,255,.06)',
                    border: listening ? '0.5px solid rgba(220,38,38,.5)' : '0.5px solid rgba(255,255,255,.12)',
                    color: listening ? '#f87171' : 'rgba(255,255,255,.5)',
                    cursor: 'pointer', fontSize: 11, fontWeight: 600,
                    animation: listening ? 'blink 1s infinite' : 'none',
                  }}
                >
                  <IconMic active={listening} />
                  {listening ? 'Arrêter' : 'Dicter'}
                </button>
              </div>
              <textarea
                value={description + (interimText ? ' ' + interimText : '')}
                onChange={e => setDescription(e.target.value)}
                placeholder="Ex : Fuite d'eau sous l'évier, eau qui coule depuis 30 min..."
                rows={4}
                style={{ ...inputStyle, resize: 'none', lineHeight: 1.5 }}
              />
              {listening && (
                <div style={{ fontSize: 11, color: '#f87171', marginTop: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#f87171', display: 'inline-block', animation: 'blink 1s infinite' }} />
                  Dictée en cours…
                </div>
              )}
            </div>

            {/* Type d'prestataire */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,.38)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 6 }}>
                Quel type d'prestataire ?
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {PRESTATAIRE_TYPES.map(t => (
                  <button key={t} type="button" onClick={() => setPrestataireType(t === prestataireType ? '' : t)}
                    style={{
                      height: 32, padding: '0 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none',
                      background: prestataireType === t ? 'rgba(5,150,105,.22)' : 'rgba(255,255,255,.05)',
                      color: prestataireType === t ? '#2E7D52' : 'rgba(255,255,255,.45)',
                      outline: prestataireType === t ? '1px solid rgba(5,150,105,.5)' : 'none',
                    }}>
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {/* Photos / vidéos */}
            <div style={{ marginBottom: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <label style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,.38)', textTransform: 'uppercase', letterSpacing: '.07em' }}>
                  Photos / vidéos (max 5 Mo)
                </label>
                <button type="button" onClick={() => fileInputRef.current?.click()}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, height: 30, padding: '0 10px', borderRadius: 8, background: 'rgba(255,255,255,.06)', border: '0.5px solid rgba(255,255,255,.12)', color: 'rgba(255,255,255,.5)', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
                  <IconPhoto />
                  Ajouter
                </button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                multiple
                onChange={handleFiles}
                style={{ display: 'none' }}
              />
              {fileError && (
                <div style={{ fontSize: 11, color: '#fca5a5', marginBottom: 6 }}>{fileError}</div>
              )}
              {files.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {files.map((f, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, height: 28, padding: '0 8px', background: 'rgba(255,255,255,.06)', border: '0.5px solid rgba(255,255,255,.1)', borderRadius: 7 }}>
                      <span style={{ fontSize: 11, color: 'rgba(255,255,255,.65)', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                      <button type="button" onClick={() => removeFile(i)}
                        style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,.3)', cursor: 'pointer', padding: 0, fontSize: 13, lineHeight: 1 }}>
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Submit */}
            <button type="submit" disabled={!description.trim()}
              style={{
                width: '100%', height: 50, borderRadius: 12,
                background: description.trim() ? '#2E7D52' : 'rgba(5,150,105,.3)',
                border: 'none', color: '#fff', fontSize: 15, fontWeight: 700,
                cursor: description.trim() ? 'pointer' : 'default',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              }}>
              <IconSend />
              Envoyer la demande urgente
            </button>
          </form>
        )}
      </div>

      <style>{`@keyframes blink { 0%,100%{opacity:1} 50%{opacity:.3} }`}</style>
    </>
  );
}
